import { useEffect } from 'react'

export const SITE_NAME = 'Places in Time'
export const SITE_BASE_URL = 'https://places-in-time.com'
export const DEFAULT_SHARE_IMAGE = `${SITE_BASE_URL}/images/home/homepagebanner.png`

type MetadataOptions = {
  title: string
  description: string
  path: string
  type?: 'website' | 'article'
  image?: string | null
}

const normalizePath = (path: string): string => {
  const trimmed = path.trim()
  if (!trimmed || trimmed === '/') {
    return '/'
  }

  return `/${trimmed.replace(/^\/+|\/+$/g, '')}`
}

export const buildCanonicalUrl = (path: string): string => {
  const normalizedPath = normalizePath(path)
  return normalizedPath === '/' ? `${SITE_BASE_URL}/` : `${SITE_BASE_URL}${normalizedPath}`
}

const toAbsoluteUrl = (value?: string | null): string => {
  if (!value) {
    return DEFAULT_SHARE_IMAGE
  }

  if (/^https?:\/\//i.test(value)) {
    return value
  }

  return `${SITE_BASE_URL}${normalizePath(value)}`
}

const upsertMetaTag = (
  selector: string,
  attributeName: 'name' | 'property',
  attributeValue: string,
  content: string,
) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector)

  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attributeName, attributeValue)
    document.head.appendChild(tag)
  }

  tag.setAttribute('content', content)
}

const upsertCanonicalLink = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')

  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }

  link.setAttribute('href', href)
}

export function usePageMetadata({
  title,
  description,
  path,
  type = 'website',
  image,
}: MetadataOptions) {
  useEffect(() => {
    const canonicalUrl = buildCanonicalUrl(path)
    const shareImage = toAbsoluteUrl(image)

    document.title = title
    upsertCanonicalLink(canonicalUrl)
    upsertMetaTag('meta[name="description"]', 'name', 'description', description)
    upsertMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME)
    upsertMetaTag('meta[property="og:title"]', 'property', 'og:title', title)
    upsertMetaTag('meta[property="og:description"]', 'property', 'og:description', description)
    upsertMetaTag('meta[property="og:type"]', 'property', 'og:type', type)
    upsertMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl)
    upsertMetaTag('meta[property="og:image"]', 'property', 'og:image', shareImage)
  }, [description, image, path, title, type])
}
